import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2, Save, X, ChevronLeft, ChevronRight, FolderOpen } from 'lucide-react';
import SharePointBrowser from '../../components/SharePointBrowser.tsx';
import { Autocomplete, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  createNcr,
  updateNcr,
  getNcrById,
  createStageDependency,
  deleteStageDependency,
  type Ncr,
  type NcrStage,
} from '../../services/ncrService.ts';
import { getAllNcrTemplates, type NcrTemplate } from '../../services/ncrTemplateService.ts';
import { getAllUsers, type User } from '../../services/userService.ts';
import './NcrEdit.css';

const NcrEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const isCreate = !id;
  const currentUserId = Number(localStorage.getItem('userId'));
  const todayDate = new Date().toISOString().split('T')[0];
  const formatStatusLabel = (value?: number | string) => {
    const n = Number(value);
    if (n === 0) return 'Pending';
    if (n === 1) return 'In Progress';
    if (n === 2) return 'Completed';
    if (n === 3) return 'Blocked';
    return 'Pending';
  };

  // Form State
  const [formData, setFormData] = useState<Partial<Ncr>>({
    ncrNumber: '',
    description: '',
    raisedDate: todayDate,
    completeDate: undefined,
    templateId: 0,
    ownerId: undefined,
    status: 'open',
    stages: [],
    properties: [],
  });

  const [templates, setTemplates] = useState<NcrTemplate[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(isCreate ? false : true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowDraftLevels, setWorkflowDraftLevels] = useState<number[]>([]);
  const [spBrowserPropIndex, setSpBrowserPropIndex] = useState<number | null>(null);

  const currentUser = users.find((u) => u.id === currentUserId);
  const isPrivilegedRole = (() => {
    const roleName = (currentUser?.roleName || '').toLowerCase();
    return roleName.includes('admin') || roleName.includes('qa manager') || roleName.includes('qamanage');
  })();

  const canEditAll = isCreate || formData.ownerId === currentUserId || isPrivilegedRole;
  const isNcrOwner = canEditAll;
  const isStageOwner = (stage: { ownerId?: number }) => canEditAll || stage.ownerId === currentUserId;
  const isActionOwner = (stage: { ownerId?: number }, action: { ownerId?: number }) =>
    canEditAll || stage.ownerId === currentUserId || action.ownerId === currentUserId;

  const toDatePickerValue = (value?: string | null) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const toDateOnlyString = (value: Date | null) => {
    if (!value) return undefined;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load templates
        const [templateList, userList] = await Promise.all([
          getAllNcrTemplates(),
          getAllUsers(),
        ]);
        setTemplates(templateList);
        setUsers(userList);

        // Load NCR if editing
        if (!isCreate && id) {
          const ncrData = await getNcrById(Number(id));
          console.log('Raw ncrData stages:', ncrData.stages);
          // Map stages with actions
          const stages = ncrData.stages?.map((stage: any) => ({
            ...stage,
            actions: stage.actions?.map((action: any) => ({
              ...action,
              completeDate: action.completedAt || action.completeDate || action.closedDate,
            })),
          }));
          console.log('Loaded stages with actions:', stages);
          setFormData({
            ...ncrData,
            stages,
            raisedDate: toDateOnlyString(toDatePickerValue(ncrData.raisedDate)),
            targetDate: toDateOnlyString(toDatePickerValue(ncrData.targetDate)),
            completeDate: toDateOnlyString(toDatePickerValue(ncrData.completeDate)),
          });
          // Auto-expand first stage for viewing
          if (ncrData.stages && ncrData.stages.length > 0) {
                setExpandedStages(new Set([ncrData.stages[0].id || 0]));
           }
        }
      } catch (error: any) {
        notify(error.message || 'Failed to load data', { type: 'warning' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isCreate, notify]);

  // Handle basic field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const selectedOwner = users.find((user) => user.id === formData.ownerId) || null;

  const selectedStageOwner = (ownerId?: number) => users.find((user) => user.id === ownerId) || null;

  // Handle template selection - load stages from template
  const handleTemplateChange = (templateId: number) => {
    const selectedTemplate = templates.find((t) => t.id === templateId);
    if (selectedTemplate) {
      const newStages: NcrStage[] = selectedTemplate.stages.map((tStage, index) => ({
        templateStageId: tStage.id,
        stageName: tStage.name,
        status: 0,
        targetDate: undefined,
        completeDate: undefined,
        detail: '',
        sortOrder: index,
        reminderOffsets: [-7],
        actions: [],
        properties: [],
      }));

      const newProperties = (selectedTemplate.properties || []).map((p) => ({
        templatePropertyId: p.id,
        propertyName: p.name,
        value: '',
        attachments: [],
      }));

      setFormData((prev) => ({
        ...prev,
        templateId,
        templateName: selectedTemplate.name,
        stages: newStages,
        properties: newProperties,
      }));

      // Clear expanded stages
      setExpandedStages(new Set());
      // Auto-expand first stage
      if (newStages.length > 0) {
        setExpandedStages(new Set([0]));
      }

      notify('Stages loaded from template', { type: 'info' });
    }
  };

  // Toggle stage expansion
  const toggleStageExpanded = (index: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedStages(newExpanded);
  };

  // Update stage field
  const updateStageField = (stageIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      stages[stageIndex] = {
        ...stages[stageIndex],
        [field]: value,
      };
      return { ...prev, stages };
    });
  };

  // Update NCR-level property
  const updateNcrProperty = (propIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      properties[propIndex] = {
        ...properties[propIndex],
        [field]: value,
      };
      return { ...prev, properties };
    });
  };

  // Add NCR-level property
  const addNcrProperty = () => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      properties.push({
        templatePropertyId: undefined,
        propertyName: '',
        value: '',
        attachments: [],
      });
      return { ...prev, properties };
    });
  };

  // Remove NCR-level property
  const removeNcrProperty = (propIndex: number) => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      properties.splice(propIndex, 1);
      return { ...prev, properties };
    });
  };

  const setNcrPropertyUrl = (propIndex: number, url: string) => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      properties[propIndex] = {
        ...properties[propIndex],
        attachments: url
          ? [{ id: Date.now(), fileName: url, filePath: url, uploadedAt: new Date().toISOString() }]
          : [],
      };
      return { ...prev, properties };
    });
  };

  // Add action to stage
  const addActionToStage = (stageIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const currentStage = stages[stageIndex];
      stages[stageIndex] = {
        ...currentStage,
        actions: [
          ...(currentStage?.actions || []),
          {
            actionNumber: currentStage?.actions?.length ? currentStage.actions.length + 1 : 1,
            targetDate: undefined,
            completeDate: undefined,
            actionDetails: '',
            actionResponse: '',
            ownerId: undefined,
            status: 'open',
            attachments: [],
          },
        ],
      };
      return { ...prev, stages };
    });
  };

  // Update stage action field
  const updateStageAction = (stageIndex: number, actionIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const actions = [...(stages[stageIndex]?.actions || [])];
      actions[actionIndex] = {
        ...actions[actionIndex],
        [field]: value,
      };
      stages[stageIndex] = {
        ...stages[stageIndex],
        actions,
      };
      return { ...prev, stages };
    });
  };

  // Remove action from stage
  const removeActionFromStage = (stageIndex: number, actionIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const actions = [...(stages[stageIndex]?.actions || [])];
      actions.splice(actionIndex, 1);
      stages[stageIndex] = {
        ...stages[stageIndex],
        actions,
      };
      return { ...prev, stages };
    });
  };

  // Workflow config modal handlers (Qpulse-style left/right indent)
  const openWorkflowConfig = () => {
    const stages = formData.stages || [];
    if (stages.length === 0) {
      notify('Please select template and load stages first', { type: 'warning' });
      return;
    }
    const initialLevels = stages.map((stage) => Math.max(0, Number(stage.workflowLevel ?? 0)));
    if (initialLevels.length > 0) {
      initialLevels[0] = 0;
    }
    setWorkflowDraftLevels(initialLevels);
    setWorkflowModalOpen(true);
  };

  const moveStageLeft = (index: number) => {
    if (index === 0) return;
    setWorkflowDraftLevels((prev) => {
      const next = [...prev];
      next[index] = Math.max(0, (next[index] ?? 0) - 1);
      return next;
    });
  };

  const moveStageRight = (index: number) => {
    if (index === 0) return;
    setWorkflowDraftLevels((prev) => {
      const next = [...prev];
      const maxAllowed = (next[index - 1] ?? 0) + 1;
      next[index] = Math.min(maxAllowed, (next[index] ?? 0) + 1);
      return next;
    });
  };

  const applyWorkflowConfig = () => {
    const stages = [...(formData.stages || [])];
    const normalizedLevels = workflowDraftLevels.map((level, index) => {
      if (index === 0) return 0;
      const maxAllowed = (workflowDraftLevels[index - 1] ?? 0) + 1;
      return Math.max(0, Math.min(level ?? 0, maxAllowed));
    });

    const nextStages: NcrStage[] = stages.map((stage, index) => {
      const level = normalizedLevels[index] ?? 0;
      let parentIndex = -1;
      if (level > 0) {
        for (let i = index - 1; i >= 0; i -= 1) {
          if ((normalizedLevels[i] ?? 0) === level - 1) {
            parentIndex = i;
            break;
          }
        }
      }
      const parentStage = parentIndex >= 0 ? stages[parentIndex] : undefined;
      return {
        ...stage,
        workflowLevel: level,
        parentStageId: parentStage?.id,
        parentTemplateStageId: parentStage?.templateStageId,
      } as NcrStage;
    });

    setFormData((prev) => ({ ...prev, stages: nextStages }));
    setWorkflowModalOpen(false);
    notify('Workflow configured — will be saved with the NCR', { type: 'info' });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.ncrNumber?.trim()) {
      notify('NCR Number is required', { type: 'warning' });
      return;
    }

    if (!formData.templateId) {
      notify('Template selection is required', { type: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        raisedDate: toDateOnlyString(toDatePickerValue(formData.raisedDate || null)),
        targetDate: toDateOnlyString(toDatePickerValue(formData.targetDate || null)),
        completeDate: toDateOnlyString(toDatePickerValue(formData.completeDate || null)),
        stages: formData.stages?.map((stage) => ({
          name: stage.stageName,
          description: stage.detail,
          templateStageId: stage.templateStageId,
          parentStageInstanceId: stage.parentStageId,
          parentTemplateStageId: stage.parentTemplateStageId,
          level: stage.workflowLevel ?? 0,
          ownerId: stage.ownerId,
          status: stage.status,
          targetDate: stage.targetDate,
          completeDate: stage.completeDate,
          sortOrder: stage.sortOrder,
          reminderOffsets: stage.reminderOffsets ?? [],
          properties: stage.properties,
          actions: stage.actions || [],
        })),
        properties: (formData.properties || []).map((prop) => ({
          templatePropertyId: prop.templatePropertyId,
          propertyName: prop.propertyName,
          value: prop.value,
          attachments: prop.attachments || [],
        })),
      };

      if (isCreate) {
        const result = await createNcr(submitData);

        // Create stage workflow dependencies derived from workflowLevel + order
        const resultStages = result.stages || [];
        const levels = (formData.stages || []).map((s) => s.workflowLevel ?? 0);
        for (let i = 1; i < resultStages.length; i++) {
          const level = levels[i] ?? 0;
          if (level > 0) {
            for (let j = i - 1; j >= 0; j--) {
              if ((levels[j] ?? 0) === level - 1) {
                const prereqId = resultStages[j]?.id;
                const depId = resultStages[i]?.id;
                if (prereqId && depId && result.id) {
                  try {
                    await createStageDependency(result.id, {
                      prerequisiteStageId: prereqId,
                      dependentStageId: depId,
                      requiredStatus: 'Completed',
                    });
                  } catch {
                    // non-fatal: dependency creation failure doesn't block NCR creation
                  }
                }
                break;
              }
            }
          }
        }

        notify('NCR created successfully', { type: 'info' });
        navigate(`/Ncr/${result.id}`);
      } else {
        const ncrId = Number(id);
        await updateNcr(ncrId, submitData);

        // Sync stage workflow dependencies
        const desiredPairs = new Set<string>();
        (formData.stages || []).forEach((s) => {
          if (s.id && s.parentStageId) {
            desiredPairs.add(`${s.parentStageId}->${s.id}`);
          }
        });

        const existingPairs = new Map<string, number>();
        (formData.stageDependencies || []).forEach((d) => {
          const key = `${d.prerequisiteStageId}->${d.dependentStageId}`;
          if (d.id) existingPairs.set(key, d.id);
        });

        for (const pair of Array.from(desiredPairs)) {
          if (!existingPairs.has(pair)) {
            const [fromStr, toStr] = pair.split('->');
            try {
              await createStageDependency(ncrId, {
                prerequisiteStageId: Number(fromStr),
                dependentStageId: Number(toStr),
                requiredStatus: 'Completed',
              });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('createStageDependency failed', err);
            }
          }
        }

        for (const [pair, depId] of existingPairs.entries()) {
          if (!desiredPairs.has(pair) && depId) {
            try {
              await deleteStageDependency(ncrId, depId);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('deleteStageDependency failed', err);
            }
          }
        }

        notify('NCR updated successfully', { type: 'info' });
        navigate(`/Ncr/${id}`);
      }
    } catch (error: any) {
      notify(error.message || 'Failed to save NCR', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <div className="text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  const stageBadgeClass = (status?: number | string) => {
    const n = Number(status);
    if (n === 1) return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
    if (n === 2) return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
    if (n === 3) return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
    return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

  return (
    <form onSubmit={handleSubmit} className="px-10 pt-5 pb-6">
      {/* Back + Title */}
      <button
        type="button"
        onClick={() => navigate('/Ncr')}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
      <h2 className="text-[22px] font-extrabold text-slate-900 leading-none mb-5">
        {isCreate ? 'Create New NCR' : 'Edit NCR'}
      </h2>

      {/* NCR Information */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">NCR Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ncrNumber" className="block text-sm font-medium text-gray-700 mb-1.5">
              NCR Number *
            </label>
            <input
              id="ncrNumber"
              type="text"
              value={formData.ncrNumber || ''}
              onChange={(e) => handleFieldChange('ncrNumber', e.target.value)}
              placeholder="e.g., NCR-2024-001"
              required
              disabled={!isNcrOwner}
              className={inputCls}
            />
          </div>

          <div>
            <label htmlFor="templateId" className="block text-sm font-medium text-gray-700 mb-1.5">
              Select Template *
            </label>
            <select
              id="templateId"
              value={formData.templateId || 0}
              onChange={(e) => handleTemplateChange(Number(e.target.value))}
              required
              disabled={!isNcrOwner}
              className={inputCls}
            >
              <option value={0}>-- Select a Template --</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Enter NCR description"
              rows={3}
              disabled={!isNcrOwner}
              className={inputCls}
            />
          </div>

          <div>
            <label htmlFor="raisedDate" className="block text-sm font-medium text-gray-700 mb-1.5">
              Raised Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                format="dd/MM/yyyy"
                value={toDatePickerValue(formData.raisedDate)}
                onChange={(newValue) => handleFieldChange('raisedDate', toDateOnlyString(newValue))}
                disabled={!isNcrOwner}
                sx={{ width: '100%' }}
                slotProps={{ textField: { id: 'raisedDate', size: 'small', inputProps: { placeholder: 'DD/MM/YYYY' } } }}
              />
            </LocalizationProvider>
          </div>

          <div>
            <label htmlFor="targetDate" className="block text-sm font-medium text-gray-700 mb-1.5">
              Target Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                format="dd/MM/yyyy"
                value={toDatePickerValue(formData.targetDate)}
                onChange={(newValue) => handleFieldChange('targetDate', toDateOnlyString(newValue))}
                disabled={!isNcrOwner}
                sx={{ width: '100%' }}
                slotProps={{ textField: { id: 'targetDate', size: 'small', inputProps: { placeholder: 'DD/MM/YYYY' } } }}
              />
            </LocalizationProvider>
          </div>

          <div>
            <label htmlFor="completeDate" className="block text-sm font-medium text-gray-700 mb-1.5">
              Complete Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                format="dd/MM/yyyy"
                value={toDatePickerValue(formData.completeDate)}
                onChange={(newValue) => handleFieldChange('completeDate', toDateOnlyString(newValue))}
                disabled={!isNcrOwner}
                sx={{ width: '100%' }}
                slotProps={{ textField: { id: 'completeDate', size: 'small', inputProps: { placeholder: 'DD/MM/YYYY' } } }}
              />
            </LocalizationProvider>
          </div>

          <div>
            <label htmlFor="severity" className="block text-sm font-medium text-gray-700 mb-1.5">
              Severity
            </label>
            <select
              id="severity"
              value={formData.severity || 'Low Risk'}
              onChange={(e) => handleFieldChange('severity', e.target.value)}
              disabled={!isNcrOwner}
              className={inputCls}
            >
              <option value="Low Risk">Low Risk</option>
              <option value="Medium Risk">Medium Risk</option>
              <option value="High Risk">High Risk</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Owner</label>
            <Autocomplete
              options={users}
              value={selectedOwner}
              onChange={(_, value) => handleFieldChange('ownerId', value?.id)}
              getOptionLabel={(option) => option.userName || option.email || String(option.id)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No users found"
              disabled={!isNcrOwner}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search and select owner" size="small" />
              )}
              sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: '0.5rem' } }}
            />
            {selectedOwner && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedOwner.roleName || 'User'}{selectedOwner.email ? ` • ${selectedOwner.email}` : ''}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
            <select
              id="status"
              value={formData.status || 'open'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!isNcrOwner}
              className={inputCls}
            >
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="close">Close</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stages Section */}
      {formData.stages && formData.stages.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Stages</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {formData.stages.length} stage{formData.stages.length !== 1 ? 's' : ''} configured
              </p>
            </div>
            <button
              type="button"
              onClick={openWorkflowConfig}
              disabled={!isNcrOwner}
              className="inline-flex items-center gap-2 px-4 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Config Workflow
            </button>
          </div>

          <div className="space-y-3">
            {formData.stages.map((stage, stageIndex) => (
              <div key={stageIndex} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Stage Header */}
                <div
                  className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-gray-200 cursor-pointer select-none hover:bg-slate-100 transition-colors"
                  onClick={() => toggleStageExpanded(stageIndex)}
                >
                  <div className="flex items-center gap-3">
                    {expandedStages.has(stageIndex) ? (
                      <ChevronUp size={18} className="text-slate-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown size={18} className="text-slate-500 flex-shrink-0" />
                    )}
                    <span
                      className="font-semibold text-slate-800 text-sm"
                      style={{ marginLeft: `${(stage.workflowLevel ?? 0) * 20}px` }}
                    >
                      Stage {stageIndex + 1}: {stage.stageName}
                    </span>
                    <span className="stage-level-badge">L{stage.workflowLevel ?? 0}</span>
                  </div>
                  <span className={stageBadgeClass(stage.status)}>
                    {formatStatusLabel(stage.status)}
                  </span>
                </div>

                {/* Stage Body */}
                {expandedStages.has(stageIndex) && (
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Owner</label>
                        <Autocomplete
                          options={users}
                          value={selectedStageOwner(stage.ownerId)}
                          onChange={(_, value) => updateStageField(stageIndex, 'ownerId', value?.id)}
                          getOptionLabel={(option) => option.userName || option.email || String(option.id)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          noOptionsText="No users found"
                          disabled={!isStageOwner(stage)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Search and select owner" size="small" />
                          )}
                          sx={{ '& .MuiOutlinedInput-root': { height: 40, borderRadius: '0.5rem' } }}
                        />
                        {selectedStageOwner(stage.ownerId) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {selectedStageOwner(stage.ownerId)!.roleName || 'User'}
                            {selectedStageOwner(stage.ownerId)!.email ? ` • ${selectedStageOwner(stage.ownerId)!.email}` : ''}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                        <select
                          value={stage.status ?? 0}
                          onChange={(e) => updateStageField(stageIndex, 'status', Number(e.target.value))}
                          disabled={!isStageOwner(stage)}
                          className={inputCls}
                        >
                          <option value={0}>Pending</option>
                          <option value={1}>In Progress</option>
                          <option value={2}>Completed</option>
                          <option value={3}>Blocked</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Target Date</label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            format="dd/MM/yyyy"
                            value={toDatePickerValue(stage.targetDate)}
                            onChange={(newValue) => updateStageField(stageIndex, 'targetDate', toDateOnlyString(newValue))}
                            disabled={!isStageOwner(stage)}
                            slotProps={{ textField: { size: 'small', fullWidth: true, inputProps: { placeholder: 'DD/MM/YYYY' } } }}
                          />
                        </LocalizationProvider>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Complete Date</label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            format="dd/MM/yyyy"
                            value={toDatePickerValue(stage.completeDate)}
                            onChange={(newValue) => updateStageField(stageIndex, 'completeDate', toDateOnlyString(newValue))}
                            disabled={!isStageOwner(stage)}
                            slotProps={{ textField: { size: 'small', fullWidth: true, inputProps: { placeholder: 'DD/MM/YYYY' } } }}
                          />
                        </LocalizationProvider>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Detail</label>
                        <textarea
                          value={stage.detail || ''}
                          onChange={(e) => updateStageField(stageIndex, 'detail', e.target.value)}
                          placeholder="Enter stage detail"
                          rows={3}
                          disabled={!isStageOwner(stage)}
                          className={inputCls}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Overdue Notifications
                          <span className="ml-1.5 text-xs font-normal text-gray-400">(linked to target date)</span>
                        </label>
                        <div className="flex flex-wrap gap-4">
                          {([
                            { offset: -14, label: '14 days before target date' },
                            { offset: -7, label: '7 days before target date' },
                            { offset: 0, label: 'On target date' },
                          ] as { offset: number; label: string }[]).map(({ offset, label }) => {
                            const checked = (stage.reminderOffsets ?? []).includes(offset);
                            return (
                              <label
                                key={offset}
                                className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer select-none text-sm transition-colors ${
                                  checked
                                    ? 'border-blue-500 bg-blue-50 text-blue-800'
                                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                                } ${!isStageOwner(stage) ? 'opacity-50 pointer-events-none' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  className="accent-blue-600"
                                  checked={checked}
                                  disabled={!isStageOwner(stage)}
                                  onChange={(e) => {
                                    const current = stage.reminderOffsets ?? [];
                                    const next = e.target.checked
                                      ? [...current, offset].sort((a, b) => a - b)
                                      : current.filter((o) => o !== offset);
                                    updateStageField(stageIndex, 'reminderOffsets', next);
                                  }}
                                />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">Actions</h4>
                        <button
                          type="button"
                          onClick={() => addActionToStage(stageIndex)}
                          disabled={!isStageOwner(stage)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-45 disabled:cursor-not-allowed disabled:pointer-events-none"
                        >
                          <Plus size={14} /> Add Action
                        </button>
                      </div>

                      {(stage.actions?.length || 0) === 0 ? (
                        <div className="text-center py-5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400">
                          No actions yet. Click "Add Action" to create one.
                        </div>
                      ) : (
                        <div className="actions-table-scroll">
                          <table className="properties-table">
                            <thead className="actions-table-head">
                              <tr>
                                <th className="actions-sticky-col actions-sticky-col-1">Action No</th>
                                <th className="actions-sticky-col actions-sticky-col-2">Target Date</th>
                                <th className="actions-sticky-col actions-sticky-col-3">Complete Date</th>
                                <th>Owner</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stage.actions?.map((action, actionIndex) => (
                                <React.Fragment key={actionIndex}>
                                  <tr>
                                    <td className="actions-sticky-col actions-sticky-col-1">
                                      <input
                                        type="number"
                                        value={action.actionNumber ?? actionIndex + 1}
                                        onChange={(e) =>
                                          updateStageAction(stageIndex, actionIndex, 'actionNumber', e.target.value ? Number(e.target.value) : undefined)
                                        }
                                        placeholder="Action no"
                                        disabled={!isActionOwner(stage, action)}
                                      />
                                    </td>
                                    <td className="actions-sticky-col actions-sticky-col-2">
                                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                          format="dd/MM/yyyy"
                                          value={toDatePickerValue(action.targetDate)}
                                          onChange={(newValue) =>
                                            updateStageAction(stageIndex, actionIndex, 'targetDate', toDateOnlyString(newValue))
                                          }
                                          disabled={!isActionOwner(stage, action)}
                                          sx={{ width: 130 }}
                                          slotProps={{ textField: { size: 'small', inputProps: { placeholder: 'DD/MM/YYYY' } } }}
                                        />
                                      </LocalizationProvider>
                                    </td>
                                    <td className="actions-sticky-col actions-sticky-col-3">
                                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                          format="dd/MM/yyyy"
                                          value={toDatePickerValue(action.completeDate)}
                                          onChange={(newValue) =>
                                            updateStageAction(stageIndex, actionIndex, 'completeDate', toDateOnlyString(newValue))
                                          }
                                          disabled={!isActionOwner(stage, action)}
                                          sx={{ width: 130 }}
                                          slotProps={{ textField: { size: 'small', inputProps: { placeholder: 'DD/MM/YYYY' } } }}
                                        />
                                      </LocalizationProvider>
                                    </td>
                                    <td>
                                      <Autocomplete
                                        options={users}
                                        value={selectedStageOwner(action.ownerId)}
                                        onChange={(_, value) =>
                                          updateStageAction(stageIndex, actionIndex, 'ownerId', value?.id)
                                        }
                                        getOptionLabel={(option) => option.userName || option.email || String(option.id)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        noOptionsText="No users found"
                                        disabled={!isActionOwner(stage, action)}
                                        renderInput={(params) => (
                                          <TextField {...params} placeholder="Owner" size="small" />
                                        )}
                                        sx={{
                                          minWidth: 180,
                                          '& .MuiOutlinedInput-root': { height: 40, borderRadius: '4px' },
                                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' },
                                          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bbb' },
                                          '& .MuiInputBase-input': { border: 0 },
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <select
                                        value={action.status || 'open'}
                                        onChange={(e) =>
                                          updateStageAction(stageIndex, actionIndex, 'status', e.target.value)
                                        }
                                        disabled={!isActionOwner(stage, action)}
                                        style={{
                                          border: 'none',
                                          outline: 'none',
                                          boxShadow: 'none',
                                          WebkitAppearance: 'none',
                                          MozAppearance: 'none',
                                          appearance: 'none',
                                          background: 'transparent',
                                          fontSize: '13px',
                                          fontFamily: 'inherit',
                                          cursor: 'pointer',
                                          color: 'inherit',
                                          padding: '0',
                                        }}
                                      >
                                        <option value="open">Open</option>
                                        <option value="in-progress">In Progress</option>
                                        <option value="close">Close</option>
                                      </select>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn-icon btn-delete"
                                        onClick={() => removeActionFromStage(stageIndex, actionIndex)}
                                        disabled={!isActionOwner(stage, action)}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="action-subrow-label">Action Details</td>
                                    <td colSpan={4}>
                                      <textarea
                                        value={action.actionDetails || ''}
                                        onChange={(e) =>
                                          updateStageAction(stageIndex, actionIndex, 'actionDetails', e.target.value)
                                        }
                                        placeholder="Action details"
                                        rows={3}
                                        disabled={!isActionOwner(stage, action)}
                                      />
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="action-subrow-label">Action Response</td>
                                    <td colSpan={4}>
                                      <textarea
                                        value={action.actionResponse || ''}
                                        onChange={(e) =>
                                          updateStageAction(stageIndex, actionIndex, 'actionResponse', e.target.value)
                                        }
                                        placeholder="Action response"
                                        rows={3}
                                        disabled={!isActionOwner(stage, action)}
                                      />
                                    </td>
                                  </tr>
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!formData.templateId && (
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-14 mb-5 text-center text-sm text-gray-400">
          Please select a template to view and configure stages
        </div>
      )}

      {/* Properties */}
      {!!formData.templateId && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Properties</h3>
            <button
              type="button"
              onClick={addNcrProperty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <Plus size={14} /> Add Property
            </button>
          </div>

          {(formData.properties?.length || 0) === 0 ? (
            <div className="text-center py-5 bg-gray-50 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400">
              No properties yet. Click "Add Property" to create one.
            </div>
          ) : (
            <table className="properties-table">
              <thead>
                <tr>
                  <th>Property Name</th>
                  <th>Value</th>
                  <th>URL / Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(formData.properties || []).map((prop, propIndex) => (
                  <tr key={propIndex}>
                    <td>
                      <input
                        type="text"
                        value={prop.propertyName || ''}
                        onChange={(e) => updateNcrProperty(propIndex, 'propertyName', e.target.value)}
                        placeholder="Property name"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={prop.value || ''}
                        onChange={(e) => updateNcrProperty(propIndex, 'value', e.target.value)}
                        placeholder="Property value"
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="text"
                          value={prop.attachments?.[0]?.filePath || ''}
                          onChange={(e) => setNcrPropertyUrl(propIndex, e.target.value)}
                          placeholder="Enter URL"
                          className="property-url-input"
                        />
                        <button
                          type="button"
                          className="btn-sm btn-primary"
                          onClick={() => setSpBrowserPropIndex(propIndex)}
                        >
                          <FolderOpen size={13} /> Browse
                        </button>
                        {prop.attachments?.[0]?.filePath && (
                          <>
                            <button
                              type="button"
                              className="btn-sm btn-primary"
                              onClick={() => window.open(prop.attachments![0].filePath, '_blank')}
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              className="btn-sm btn-secondary"
                              onClick={() => setNcrPropertyUrl(propIndex, '')}
                            >
                              <X size={13} /> Clear
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-icon btn-delete"
                        onClick={() => removeNcrProperty(propIndex)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <button
          type="button"
          onClick={() => navigate('/Ncr')}
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 border border-gray-300 text-slate-700 rounded-[10px] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
        >
          <X size={18} /> Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 h-10 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
        >
          <Save size={18} /> {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Workflow Modal */}
      {workflowModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content workflow-modal">
            <div className="modal-header">
              <h3>Config Workflow</h3>
              <button type="button" className="btn-close" onClick={() => setWorkflowModalOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="workflow-tip">
                Use left/right buttons to set hierarchy. Right = child level, Left = parent level.
              </p>
              <div className="workflow-list">
                {(formData.stages || []).map((stage, index) => {
                  const level = workflowDraftLevels[index] ?? 0;
                  const maxRight = index === 0 ? 0 : (workflowDraftLevels[index - 1] ?? 0) + 1;
                  return (
                    <div key={`${stage.templateStageId || stage.id || index}-${index}`} className="workflow-row">
                      <div className="workflow-row-actions">
                        <button
                          type="button"
                          className="btn-icon workflow-indent-btn"
                          onClick={() => moveStageLeft(index)}
                          disabled={index === 0 || level === 0}
                          title="Move Left"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon workflow-indent-btn"
                          onClick={() => moveStageRight(index)}
                          disabled={index === 0 || level >= maxRight}
                          title="Move Right"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                      <div className="workflow-stage-name" style={{ marginLeft: `${level * 24}px` }}>
                        <span className="workflow-stage-order">{index + 1}.</span>
                        <span>{stage.stageName || `Stage ${index + 1}`}</span>
                        <span className="workflow-level-tag">L{level}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setWorkflowModalOpen(false)}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={applyWorkflowConfig}>
                Apply Workflow
              </button>
            </div>
          </div>
        </div>
      )}

      {spBrowserPropIndex !== null && (
        <SharePointBrowser
          onPick={(url) => {
            setNcrPropertyUrl(spBrowserPropIndex, url);
            setSpBrowserPropIndex(null);
          }}
          onClose={() => setSpBrowserPropIndex(null)}
        />
      )}
    </form>
  );
};

export default NcrEdit;
